import React, { useState, useEffect, useMemo } from "react"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Send } from "lucide-react"

import styles from "./SendQuotePopup.module.scss"
import ModalOverlay from "../../UI/ModalOverlay/ModalOverlay.tsx"
import CustomSelect from "../../../pages/ProjectDocumentation/components/CustomSelect.tsx"
import LocationWithJurisdiction from "../../UI/LocationWithJurisdiction/LocationWithJurisdiction.tsx"
import { resetSuccess, sendQuote } from "../../../store/actions/categoryActions.js"
import useGetTodayDate from "../../../hooks/useGetTodayDate.js"
import { fireAnalytics } from "../../../utils/firebase.js"

const variants = {
  visible: {
    scale: 1,
    opacity: 1,
  },
  hidden: {
    scale: 0.95,
    opacity: 0,
  },
}

interface QuoteData {
  name: string
  city: string
  receiveFrom: string
  receiveTo: string
  currency: string
  description: string
  jurisdiccion: string
  zone: string
}

interface SendQuotePopupProps {
  onClose: () => void
  onSuccess: () => void
  sendQuote: (quoteData: any, materials: any[], navigate: ReturnType<typeof useNavigate>) => Promise<void>
  materials: any[]
  setShowPreview: (value: boolean) => void
}

const getSuggestedQuoteName = (todayDate: string) => {
  if (!todayDate) return ""
  return `Cotización ${todayDate}`
}

const SendQuotePopup: React.FC<SendQuotePopupProps> = ({
  onClose,
  sendQuote,
  materials,
  onSuccess,
  setShowPreview,
}) => {
  const todayDate = useGetTodayDate()
  const suggestedName = useMemo(() => getSuggestedQuoteName(todayDate), [todayDate])
  const [hasInitializedName, setHasInitializedName] = useState(false)

  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [quoteData, setQuoteData] = useState<QuoteData>({
    name: "",
    city: "",
    receiveFrom: "",
    receiveTo: "",
    currency: "ARS",
    description: "",
    jurisdiccion: "",
    zone: "",
  })

  const navigate = useNavigate()

  useEffect(() => {
    if (suggestedName && !hasInitializedName && !quoteData.name) {
      setQuoteData((prev) => ({ ...prev, name: suggestedName }))
      setHasInitializedName(true)
    }
  }, [suggestedName, hasInitializedName, quoteData.name])

  const currencyOptions = useMemo(
    () => [
      { value: "ARS", label: "Pesos argentinos" },
      { value: "USD", label: "Dólares estadounidenses" },
    ],
    [],
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target
    setQuoteData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleCurrencyChange = (value: string) => {
    setQuoteData((prev) => ({
      ...prev,
      currency: value,
    }))
  }

  const handleLocationChange = (address: string, jurisdictionId: number | null) => {
    setQuoteData((prev) => ({
      ...prev,
      city: address,
      jurisdiccion: jurisdictionId !== null ? jurisdictionId.toString() : prev.jurisdiccion,
    }))
  }

  const handleJurisdictionChange = (value: string) => {
    setQuoteData((prev) => ({
      ...prev,
      jurisdiccion: value,
    }))
  }

  const convertDateToISOFormat = (date: string) => {
    if (!date) {
      return ""
    }
    const [dd, mm, yyyy] = date.split("/")
    return `${yyyy}-${mm}-${dd}`
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setShowPreview(false)

    try {
      const payload = {
        ...quoteData,
        jurisdiccion: quoteData.jurisdiccion ? Number(quoteData.jurisdiccion) : null,
      }

      await sendQuote(payload, materials, navigate)
      fireAnalytics("send_quotation_finish")
      onSuccess()
      onClose()
    } catch (error) {
      console.error("Error al enviar la cotización:", error)
    }
  }

  useEffect(() => {
    const isValid =
      quoteData.name &&
      quoteData.city &&
      quoteData.jurisdiccion &&
      quoteData.receiveFrom &&
      quoteData.receiveTo &&
      quoteData.description

    setIsButtonDisabled(!isValid)
  }, [quoteData])

  return (
    <ModalOverlay isOpen onClose={onClose} preventClose zIndex={32}>
      <motion.div variants={variants} initial="hidden" animate="visible" className={styles.popup}>
        <div className={styles.popup_header}>
          <div>
            <h2 className={styles.popup_header_title}>Enviar para cotizar</h2>
            <p className={styles.popup_header_subtitle}>
              Completá estos datos para que proveedores te envíen presupuestos
            </p>
          </div>
          <button type="button" onClick={onClose} className={styles.popup_header_cancelBtn}>
            Cancelar
          </button>
        </div>
        <form className={styles.popup_form} onSubmit={handleSubmit}>
          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Identificación</h3>
            <div className={styles.formGroup}>
              <label htmlFor="name">Nombre de la cotización *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={quoteData.name}
                onChange={handleInputChange}
                placeholder="Ej: Obra casa Martínez, Refacción baño"
                required
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Ubicación</h3>
            <LocationWithJurisdiction
              addressValue={quoteData.city}
              addressOnChange={(value) => setQuoteData((prev) => ({ ...prev, city: value }))}
              jurisdictionValue={quoteData.jurisdiccion}
              jurisdictionOnChange={handleJurisdictionChange}
              onPlaceSelect={handleLocationChange}
              placeholder="Ej: Pilar, Palermo, Córdoba"
              jurisdictionPlaceholder="Seleccionar provincia"
            />
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Plazo</h3>
            <p className={styles.formHelpText}>
              Definí el período en que querés recibir ofertas. Después de esa fecha dejarán de llegar presupuestos.
            </p>
            <div className={styles.formGroup_inline}>
              <div className={styles.datePickerWrapper}>
                <label htmlFor="receiveFrom">Recibir desde *</label>
                <input
                  className={styles.datePicker}
                  id="receiveFrom"
                  name="receiveFrom"
                  type="date"
                  value={quoteData.receiveFrom}
                  onChange={(event) => {
                    handleInputChange(event)
                    if (quoteData.receiveTo && new Date(event.target.value) > new Date(quoteData.receiveTo)) {
                      setQuoteData((prev) => ({ ...prev, receiveTo: "" }))
                    }
                  }}
                  max={quoteData.receiveTo || undefined}
                  min={convertDateToISOFormat(todayDate)}
                  required
                />
              </div>
              <div className={styles.datePickerWrapper}>
                <label htmlFor="receiveTo">Hasta *</label>
                <input
                  className={styles.datePicker}
                  id="receiveTo"
                  name="receiveTo"
                  type="date"
                  value={quoteData.receiveTo}
                  onChange={(event) => {
                    handleInputChange(event)
                    if (quoteData.receiveFrom && new Date(event.target.value) < new Date(quoteData.receiveFrom)) {
                      setQuoteData((prev) => ({ ...prev, receiveFrom: "" }))
                    }
                  }}
                  min={quoteData.receiveFrom || convertDateToISOFormat(todayDate)}
                  required
                />
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Condiciones</h3>
            <div className={styles.formGroup}>
              <label htmlFor="currency">Moneda *</label>
              <CustomSelect value={quoteData.currency} onChange={handleCurrencyChange} options={currencyOptions} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="description">Instrucciones o aclaraciones para el proveedor *</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={quoteData.description}
                onChange={handleInputChange}
                placeholder="Ej: Incluir flete, entrega a domicilio, preferencia por materiales específicos"
              />
              <p className={styles.formHelpText}>
                Usá este espacio para indicar condiciones como flete incluido, entrega, forma de pago u otras preferencias.
              </p>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button disabled={isButtonDisabled} type="submit" className={styles.sendBtn}>
              <Send size={16} />
              Publicar en edify
            </button>
          </div>
        </form>
      </motion.div>
    </ModalOverlay>
  )
}

const mapStateToProps = (state: any) => ({
  success: state.category.success,
  error: state.category.error,
  loading: state.category.loading,
})

export default connect(mapStateToProps, { sendQuote, resetSuccess })(SendQuotePopup)
